import React, { useEffect, useState, ReactNode } from 'react'
import { IPage, IPageComponentProps } from './Interfaces'

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import AuthService, { isRefreshTokenExpired, updateAuthLocalStorage } from '../auth/AuthService';
import windowLocationRedirect from '../redirect';
import UserService, { IUserModel } from '../../services/user';
import { useDispatch } from 'react-redux';
import { currentUserDetails, updateUser } from '../user/userSlice';
import UnauthorizedBlock from '../../components/UnauthorizedBlock';
import { handleRequestError } from '../../services/api';
import { useLocation } from 'react-router-dom';
import UserSideModal from '../user/UserSideModal';
import Register2FA from '../auth/Register2FA';
import MaintenancePage from './MaintenancePage';
import { updateGlobalLoaderStatus } from '../notifications/notificationsSlice';

interface IProps {
    children: ReactNode,
    isAdminOnly: boolean,
    redirectIfUnauthorized: boolean,
    CustomErrorPage?: () => JSX.Element
}

const ProtectedPage: React.FC<IProps> = ({ children, ...props }) => {

    const { isAdminOnly, redirectIfUnauthorized, CustomErrorPage } = props

    const dispatch = useDispatch()
    const location = useLocation()

    const { isAuthenticated, tokenRefreshedTime } = useAppSelector((state) => state.auth)
    const user = useAppSelector((state) => state.user.details)

    const [isUnauthorized, setIsUnauthorized] = useState<boolean>(false)
    const [resgister2FA, setRegister2FA] = useState<boolean>(false)

    const [unauthorizedMessage, setUnauthorizedMessage] = useState<{ title: string, message: string }>({ title: "", message: "" })

    const [attempts, setAttempts] = useState<number>(0)
    const [maintenance, setMaintenance] = useState<boolean>(false)

    const showGlobalPreloader = () => {
        // dispatch(updateGlobalLoaderStatus({ status: true }))
    }
    // const hideGlobalPreloader = () => setTimeout(() => dispatch(updateGlobalLoaderStatus({ status: false })), 3000)
    const hideGlobalPreloader = () => {
        // updateGlobalLoaderStatus({ status: false })
    }


    /**
     * Verify token
     */

    const sendRequestToVerifyToken = (requestNum: number = 0) => {
        AuthService.verifyAccessToken()
            .then(response => {
                // token is valid
                setAttempts(0)
                hideGlobalPreloader()
            })
            .catch(error => {
                
                const status = error.response.status
                // const message = error.response.data.message ? error.response.data.message : "Error"

                switch(status){

                    case 401:
                        showGlobalPreloader()
                        AuthService.refreshAccessToken()
                            .then(response => {
                                // console.log("success", response)
                                updateAuthLocalStorage()
                                setAttempts(0)
                                hideGlobalPreloader()
                            })
                            .catch(error => {
                                console.log("error AuthService.refreshAccessToken(): ", error, "attempts: ", requestNum)
                                // windowLocationRedirect("sign-in?back=" + window.location.pathname + window.location.search)

                                // v2-0-1 edit
                                if (redirectIfUnauthorized){
                                    windowLocationRedirect("logout?back=" + window.location.pathname + window.location.search)
                                }

                                if (isAuthenticated){
                                    windowLocationRedirect("logout?back=" + window.location.pathname + window.location.search)
                                }

                                // windowLocationRedirect("logout?back=" + window.location.pathname + window.location.search)

                                setAttempts(requestNum)
                                hideGlobalPreloader()
                            })

                        break

                    default:
                        if (redirectIfUnauthorized){
                            windowLocationRedirect("logout")
                        }
                }


            })
    }

    const verifyToken = (requestNum: number = 0) => {
        if (!isRefreshTokenExpired()){
            sendRequestToVerifyToken(requestNum)
        }
    }

    /**
     * Load user details
     */
    const loadUserDetails = () => {
        UserService.getCurrentUserData()
            .then(response => {
                dispatch(updateUser(response))
            })
            .catch(error => {

                const { response } = error
                const status = response.status

                switch(status){

                    case 403:
                        setRegister2FA(true)
                        break

                    case 404:
                        setIsUnauthorized(true)
                        setUnauthorizedMessage({ title: "No Access", message: response.data.message ? response.data.message : "Your account has been deleted" })
                        break

                    case 406:
                        setIsUnauthorized(true)
                        setUnauthorizedMessage({ title: "No Access", message: response.data.message ? response.data.message : "Your account has been blocked" })
                        break

                    case 409:
                        setIsUnauthorized(true)
                        setUnauthorizedMessage({ title: "No Access", message: response.data.message ? response.data.message : "You have to re-login to your account. Because your password has been changed" })
                        break

                    case 410:
                        setIsUnauthorized(true)
                        setUnauthorizedMessage({ title: "No Access", message: response.data.message ? response.data.message : "Your password is expired. You have to update it" })
                        break     
                                         
                    case 411:
                        setIsUnauthorized(true)
                        setUnauthorizedMessage({ title: "You're Blocked", message: response.data.message ? response.data.message : "Your account has been banned" })
                        break      

                    case 503:
                        
                        if (location.pathname !== "/sign-in"){
                            setMaintenance(true)
                        }

                        break

                    default:
                        handleRequestError(error, dispatch)
                }

            })
    }

    /**
     * Refresh Token
     */
    useEffect(() => {
        sendRequestToVerifyToken()
        
        loadUserDetails()

        let i = attempts;

        const intervalId = setInterval(() => {
            i++;
            verifyToken(i);
    
            if (i >= 3) {
                clearInterval(intervalId);
            }
        }, 10000);
    
        return () => clearInterval(intervalId);
    }, [])

    /**
     * Update user details if page is changed
     */
    useEffect(() => {
        loadUserDetails()
        // showGlobalPreloader()
    }, [location.pathname])

    // useEffect(() => {
    //     if (isAuthenticated){
    //         setIsUnauthorized(true)
    //     }else[
    //         setIsUnauthorized(false)
    //     ]
    // }, [isAuthenticated])

    /**
     * Check user
     */
    useEffect(() => {

        let access: boolean = true
        let title: string = ""
        let message: string = ""

        if (isAdminOnly){
            if (!user.is_superuser){
                access = false
            }
        }

        /**
         * Check if user blocked
         */
        if (user.is_blocked){
            access = false
            title = "No Access"
            message = "Your account has been blocked"
        }

        if (!access){
            setIsUnauthorized(true)
            setUnauthorizedMessage({ title: title, message: message })
        }else{
            setIsUnauthorized(false)
            setUnauthorizedMessage({ title: "", message: "" })
        }
    }, [user])

    /**
     * Maintenance
     */
    if (maintenance){
        return <MaintenancePage />
    }

    /**
     * 2FA
     */
    if (resgister2FA){
        return(
            <div className="login-page-container">
                <div className="content-of-login-page-container">
                    <Register2FA />
                </div>
            </div>
        )
    }

    /**
     * If not authenticated
     */
    if (isUnauthorized){

        if (CustomErrorPage){
            return <CustomErrorPage />
        }

        return <UnauthorizedBlock title={unauthorizedMessage.title} message={unauthorizedMessage.message} />
    }

    if (!isAuthenticated){

        if (CustomErrorPage){
            return <CustomErrorPage />
        }

        return <UnauthorizedBlock />
    }

    return(
        <div>
            <UserSideModal />
            {children}
        </div>
    )

}
export default ProtectedPage